import { ExperienceMapper } from '@ninetailed/experience.js-utils'

import {
  ExperienceMapper as ExperienceMapperContentful,
  type ExperienceEntryLike,
  AudienceMapper,
  type AudienceEntryLike,
} from '@ninetailed/experience.js-utils-contentful'

export type RenderingMode = 'Tracked' | 'Routing Only'

/**
 * `renderingMode` is set on the Ninetailed experience in Contentful and decides how a "can experiment"
 * component participates in the test:
 * - `Tracked` (default): the variant renders in place and an impression is recorded when the component is
 *   actually visible. Use when the component's content is what's being tested. The mechanism varies by
 *   component: always-visible components use `<Experience>`'s IntersectionObserver, while components that can
 *   sit in the viewport while hidden (e.g. mega-menu cards) resolve with `useExperience` and fire a manual,
 *   visibility-gated `trackComponentView` so hidden components don't log phantom impressions.
 * - `Routing Only`: resolve the variant with `useExperience` (which fires no impression) and instead track the
 *   impression on click via `trackComponentView`, so exposure is counted at the routing decision point for
 *   baseline and variant alike.
 *
 * Both modes emit the same `HAS_SEEN_ELEMENT` event, so they report into the same datamodel. Absent/unknown
 * values fall back to `Tracked`, so existing experiences keep their current behaviour.
 */
export const getRenderingMode = (experiences: any): RenderingMode =>
  experiences?.[0]?.fields?.renderingMode === 'Routing Only' ? 'Routing Only' : 'Tracked'

export const mapNtExperiences = (experiences: any) => {
  if (!experiences || !experiences[0]) return []
  return (
    experiences &&
    experiences
      .map((experience: any) => ({
        id: experience.sys.id,
        name: experience.fields?.nt_name || null,
        type: experience.fields?.nt_type,
        components: experience.fields?.nt_config.components,
        config: experience.fields?.nt_config,
        trafficAllocation: experience.fields?.nt_config.traffic,
        audience: {
          id: experience.fields?.nt_audience?.fields?.nt_audience_id || null,
          name: experience.fields?.nt_audience?.fields?.nt_name || null,
        },
        variants:
          experience.fields?.nt_variants?.map((variant: any) => ({
            ...variant.fields,
            contentful_id: variant.sys.id,
            id: variant.sys.id,
          })) || 'no variants',
      }))
      .filter(ExperienceMapper.isExperienceEntry)
      .map(ExperienceMapper.mapExperience)
  )
}

export const mapNtContentfulExperiences = (experiences) => {
  return experiences
    ? experiences.filter(ExperienceMapperContentful.isExperienceEntry).map(ExperienceMapperContentful.mapExperience)
    : []
}

export const mapNtContentfulAudiences = (audiences: any) => {
  return audiences ? audiences.filter(AudienceMapper.isAudienceEntry).map(AudienceMapper.mapAudience) : []
}
